import { useEffect } from 'react';

/**
 * Light-touch client-side anti-inspect for the public site.
 *
 *  WHAT IT DOES
 *  ────────────
 *  • Disables the browser context menu (right-click) on the page.
 *  • Blocks the common DevTools keyboard shortcuts:
 *      F12, Ctrl/Cmd+Shift+I, Ctrl/Cmd+Shift+J, Ctrl/Cmd+Shift+C, Ctrl/Cmd+U
 *  • Detects when DevTools is open (large window↔viewport delta) and reveals a
 *    full-page "Access blocked" overlay until DevTools is closed.
 *  • Disables text selection + image dragging globally for a cleaner UX.
 *
 *  ⚠ HONEST LIMITATIONS — read this:
 *  ────────────────────────────────
 *  No client-side protection is unbypassable. A sufficiently determined user
 *  can disable JS, attach a remote debugger, or use the Chrome DevTools
 *  Protocol from outside the page. The real security of this app comes from:
 *    – JWT auth + bcrypt password hashing on the backend
 *    – Tenant-scoped data access on every request
 *    – CORS lockdown + rate-limit on sensitive endpoints
 *  This module just makes casual snooping harder and signals intent.
 *
 *  The /admin route is exempt — admins legitimately need DevTools to debug.
 */
export default function useSecurityShield() {
  useEffect(() => {
    // Applied EVERYWHERE (including /admin) — user requested full protection.
    // Admins who legitimately need DevTools can temporarily disable JS or use
    // the Chrome DevTools Protocol from outside the page.

    const onContext = (e) => { e.preventDefault(); return false; };

    const onKey = (e) => {
      const key = (e.key || '').toLowerCase();
      // F12
      if (key === 'f12') { e.preventDefault(); return false; }
      const ctrl = e.ctrlKey || e.metaKey;
      // Ctrl+U (View source)
      if (ctrl && key === 'u') { e.preventDefault(); return false; }
      // Ctrl+S (Save page) — block to discourage offline scraping
      if (ctrl && key === 's') { e.preventDefault(); return false; }
      // Ctrl+Shift+I / J / C  (DevTools / Console / Inspect)
      if (ctrl && e.shiftKey && ['i', 'j', 'c'].includes(key)) {
        e.preventDefault(); return false;
      }
    };

    // DevTools open detector — DESKTOP-ONLY.
    //
    // We can't reliably detect DevTools on mobile / tablet because the address
    // bar collapsing on scroll, virtual keyboard, and pinch-zoom all cause
    // large outerWidth↔innerWidth deltas that look identical to DevTools
    // docking. False-positives there would freeze the page behind a full-screen
    // overlay. So we skip the detector entirely on touch devices.
    const isTouch = (typeof window !== 'undefined') && (
      'ontouchstart' in window
      || (navigator.maxTouchPoints || 0) > 0
      || window.matchMedia?.('(pointer: coarse)').matches
    );
    let warned = false;
    const ensureOverlay = () => {
      let el = document.getElementById('sec-block-overlay');
      if (el) return el;
      el = document.createElement('div');
      el.id = 'sec-block-overlay';
      el.setAttribute('data-testid', 'sec-block-overlay');
      el.style.cssText = [
        'position:fixed', 'inset:0', 'z-index:2147483647',
        'background:#0a0a0a', 'color:#e8e8e8',
        'display:flex', 'flex-direction:column', 'align-items:center', 'justify-content:center',
        'font-family:system-ui,-apple-system,Segoe UI,Roboto,sans-serif',
        'text-align:center', 'padding:24px'
      ].join(';');
      el.innerHTML = `
        <div style="font-size:64px;line-height:1">🔒</div>
        <h1 style="font-size:22px;margin:16px 0 8px;letter-spacing:0.08em;color:#d4d77a">ACCESS BLOCKED</h1>
        <p style="font-size:13px;color:#9a9a9a;max-width:420px">
          DevTools detected. Please close the developer console to continue browsing this site.
        </p>`;
      document.body.appendChild(el);
      return el;
    };

    const checkDevtools = () => {
      if (isTouch) return; // never run on mobile / tablet
      // BOTH dimensions must show a large delta — protects against narrow
      // edge cases on desktops with collapsed sidebars / address-bar quirks.
      const wDelta = window.outerWidth - window.innerWidth;
      const hDelta = window.outerHeight - window.innerHeight;
      // Threshold raised to 200px to reduce false positives on zoomed
      // browsers and laptops with small viewports.
      const opened = wDelta > 200 && hDelta > 200;
      const el = document.getElementById('sec-block-overlay');
      if (opened) {
        if (!warned) { warned = true; ensureOverlay(); }
      } else if (el) {
        el.remove();
        warned = false;
      }
    };

    const onDragStart = (e) => { if (e.target.tagName === 'IMG') e.preventDefault(); };

    document.addEventListener('contextmenu', onContext);
    document.addEventListener('keydown', onKey);
    document.addEventListener('dragstart', onDragStart);
    const iv = setInterval(checkDevtools, 800);

    // Cleaner UX: disable text selection on non-input elements
    const style = document.createElement('style');
    style.id = 'sec-shield-style';
    style.textContent = `
      body { -webkit-user-select: none; user-select: none; }
      input, textarea, [contenteditable="true"] { -webkit-user-select: text; user-select: text; }
      img { -webkit-user-drag: none; user-drag: none; }
    `;
    document.head.appendChild(style);

    return () => {
      document.removeEventListener('contextmenu', onContext);
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('dragstart', onDragStart);
      clearInterval(iv);
      style.remove();
      document.getElementById('sec-block-overlay')?.remove();
    };
  }, []);
}
